import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react";
import { categories } from "../mockdata/categoryData";

export default function Dropdown() {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleToggleCategory = (category: string) => {
    setActiveCategory(activeCategory === category ? null : category);
    if (hoverTimeout) clearTimeout(hoverTimeout);
  };

  const handleMouseEnter = (category: string) => {
    if (hoverTimeout) clearTimeout(hoverTimeout);
    setActiveCategory(category);
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => setActiveCategory(null), 300);
    setHoverTimeout(timeout as NodeJS.Timeout);
  };

  useEffect(() => {
    return () => {
      if (hoverTimeout) clearTimeout(hoverTimeout);
    };
  }, [hoverTimeout]);

  const renderItems = (items: any[], linkPrefix: string) => {
    return items.map((item) => {
      if (typeof item === "string") {
        return (
          <li key={item} className="cursor-pointer text-gray-500 hover:text-gray-900 leading-relaxed">
            {item}
          </li>
        );
      } else if (item.title) {
        return (
          <div key={item.title} className="mt-2">
            <span className="text-[10px] font-semibold text-gray-800">{item.title}</span>
            <ul className="ml-2 text-[10px] text-gray-500">
              {item.items.map((subItem: string) => (
                <li key={subItem} className="cursor-pointer hover:text-gray-900">
                  <Link to={`${linkPrefix}/${subItem.toLowerCase().replace(/\s+/g, "-")}`}>
                    {subItem}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        );
      }
      return null;
    });
  };

  return (
    <div className="relative bg-white border-b border-gray-200">
      {/* Desktop Navigation */}
      <nav className="hidden md:block">
        <div className="relative flex items-center justify-center w-full h-9 gap-6 md:gap-4 font-medium bg-white">
          {categories.map((category) => (
            <div
              key={category.name}
              onMouseEnter={() => handleMouseEnter(category.name)}
              onMouseLeave={handleMouseLeave}
            >
              <Link to={category.link}>
                <p
                  className={`cursor-pointer text-[12px] whitespace-nowrap transition-colors ${
                    activeCategory === category.name
                      ? "text-orange-500 font-semibold"
                      : "text-gray-700 hover:text-orange-500"
                  }`}
                >
                  {category.name}
                </p>
              </Link>

              {/* Dropdown Panel */}
              {activeCategory === category.name && (
                <div className="absolute left-0 w-full bg-white shadow-xl border-t border-gray-100 top-full z-50 md:p-8">
                  <div className="flex gap-0">

                    {/* Left — category image */}
                    <div className="w-[220px] shrink-0">
                      <img
                        src={category.image}
                        alt={category.name}
                        className="w-full h-full object-cover"
                        style={{ minHeight: "200px", maxHeight: "420px" }}
                      />
                    </div>

                    {/* Right — subcategories grid */}
                    <div className="flex-1 px-8 py-6 overflow-y-auto max-h-[420px]">
                      <div
                        className="grid gap-x-8 gap-y-6"
                        style={{
                          gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                        }}
                      >
                        {category.subcategories.map((sub) => (
                          <div key={sub.title}>
                            <Link to={sub.link}>
                              <span className="text-[12px] font-semibold text-gray-900 hover:text-orange-500 cursor-pointer block mb-2">
                                {sub.title}
                              </span>
                            </Link>
                            <ul className="text-[11px] space-y-1">
                              {renderItems(sub.items, sub.link)}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>

                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </nav>

      {/* Mobile Navigation */}
      <div className="md:hidden">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="flex items-center justify-between w-full px-5 py-3.5 text-[14px] font-bold text-[#1a2b3c] bg-[#f0f3f7]"
        >
          <span>Browse Categories</span>
          {mobileMenuOpen ? (
            <ChevronUp className="w-5 h-5 text-gray-800" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-800" />
          )}
        </button>

        {mobileMenuOpen && (
          <div className="bg-white shadow-md">
            {categories.map((category) => (
              <div key={category.name} className="border-b border-gray-200">
                <button
                  onClick={() => handleToggleCategory(category.name)}
                  className="flex items-center justify-between w-full px-4 py-3 text-sm font-medium text-gray-800 hover:bg-gray-50"
                  aria-expanded={activeCategory === category.name}
                >
                  <Link to={category.link} onClick={(e) => e.stopPropagation()}>
                    {category.name}
                  </Link>
                  {activeCategory === category.name ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>

                {activeCategory === category.name && (
                  <div className="bg-gray-50">
                    {/* Mobile image */}
                    <img
                      src={category.image || "/placeholder.svg"}
                      alt={category.name}
                      className="w-full h-36 object-cover"
                      loading="lazy"
                    />

                    {/* Mobile subcategories */}
                    <div className="px-4 py-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
                      {category.subcategories.map((sub) => (
                        <div key={sub.title} className="mb-2">
                          <Link
                            to={sub.link}
                            className="text-[12px] font-semibold text-gray-900 hover:text-orange-500 block mb-1"
                          >
                            {sub.title}
                          </Link>
                          <ul className="space-y-0.5 text-[11px]">
                            {renderItems(sub.items.slice(0, 5), sub.link)}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}